import React, { useState, useRef, useEffect } from 'react';
import { getApiErrorMessage } from '../lib/apiError';
import { useNavigate } from 'react-router-dom';
import { Box, Button, TextField, Alert, CircularProgress } from '@mui/material';
import {
  useAuth,
  SESSION_EXPIRED_MESSAGE,
  TwoFactorSessionExpiredError,
} from '../contexts/AuthContext';
import { twoFactorService } from '../services/twoFactorService';
import { getApiErrorCode } from '../lib/apiError';
import { AuthLayout } from '../components/AuthLayout';

export const TwoFactorPage: React.FC = () => {
  const navigate = useNavigate();
  const { verifyTwoFactor } = useAuth();

  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(60);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const tempSessionId = sessionStorage.getItem('tempSessionId');
    if (!tempSessionId) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setInterval(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [resendCountdown]);

  /** Leave the challenge screen: the session is gone and retrying cannot help. */
  const returnToLogin = (message: string) => {
    sessionStorage.removeItem('tempSessionId');
    navigate('/login', { replace: true, state: { message } });
  };

  const submitCode = async (code: string) => {
    setLoading(true);
    setError('');
    try {
      await verifyTwoFactor(code);
      navigate('/');
    } catch (err) {
      if (err instanceof TwoFactorSessionExpiredError) {
        returnToLogin(err.message);
        return;
      }
      setError(err instanceof Error ? err.message : 'Invalid code');
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;

    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (value && index === 5) {
      const code = newDigits.join('');
      if (code.length === 6) {
        submitCode(code);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      e.preventDefault();
      const newDigits = pasted.split('');
      setDigits(newDigits);
      submitCode(pasted);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setError('');

    const tempSessionId = sessionStorage.getItem('tempSessionId');
    if (!tempSessionId) {
      returnToLogin(SESSION_EXPIRED_MESSAGE);
      return;
    }

    try {
      // The server reissues the session with the new code so the two expire
      // together. Store the replacement, or the next attempt uses a stale one.
      const refreshed = await twoFactorService.resendLoginCode(tempSessionId);
      sessionStorage.setItem('tempSessionId', refreshed.tempSessionId);
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      setResendCountdown(60);
    } catch (err) {
      if (getApiErrorCode(err) === 'AUTH_2FA_SESSION_EXPIRED') {
        returnToLogin(SESSION_EXPIRED_MESSAGE);
        return;
      }
      setError(getApiErrorMessage(err, 'Failed to resend code'));
    } finally {
      setResendLoading(false);
    }
  };

  const handleManualSubmit = () => {
    const code = digits.join('');
    if (code.length === 6) {
      submitCode(code);
    }
  };

  return (
    <AuthLayout
      title="Verification Code"
      subtitle="A 6-digit code has been sent to your email. Enter it below."
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 3 }} onPaste={handlePaste}>
        {digits.map((digit, index) => (
          <TextField
            key={index}
            inputRef={(el) => {
              inputRefs.current[index] = el;
            }}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            disabled={loading}
            inputProps={{
              maxLength: 1,
              style: { textAlign: 'center', fontSize: '1.5rem', padding: '12px 0', width: '40px' },
              inputMode: 'numeric',
            }}
            variant="outlined"
          />
        ))}
      </Box>

      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={handleManualSubmit}
        disabled={loading || digits.join('').length !== 6}
        sx={{ mb: 2 }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
      </Button>

      <Button
        variant="text"
        size="small"
        onClick={handleResend}
        disabled={resendCountdown > 0 || resendLoading}
        sx={{ display: 'block', mx: 'auto', mb: 1 }}
      >
        {resendLoading
          ? 'Sending...'
          : resendCountdown > 0
            ? `Resend code in ${resendCountdown}s`
            : 'Resend code'}
      </Button>

      <Button
        variant="text"
        size="small"
        color="inherit"
        onClick={() => navigate('/login')}
        sx={{ display: 'block', mx: 'auto' }}
      >
        Back to Login
      </Button>
    </AuthLayout>
  );
};
